import type { CSSProperties } from 'react'
import type { SxProps } from '@mui/material'
import type { ThemedStyle } from '../types'

/**
 * Utility functions and constants
 * Transitions, z-index constants, breakpoints, and responsive utilities
 */

// Transition utilities - Standard (0.3s)
export const $transition = (properties?: string[]): ThemedStyle<CSSProperties> => ({ timing }) => {
  // Convert timing.quick (ms) to seconds for CSS transition
  const duration = `${timing.quick / 1000}s ease`
  
  return {
    transition: properties 
      ? properties.map(p => `${p} ${duration}`).join(', ')
      : `all ${duration}`,
  }
}

export const $sxTransition: ThemedStyle<SxProps> = () => ({
  transition: 'all 0.3s ease',
})

// Transition utilities - Fast (0.15s)
export const $transitionFast = (properties?: string[]): ThemedStyle<CSSProperties> => () => {
  const duration = '0.15s ease'
  return {
    transition: properties 
      ? properties.map(p => `${p} ${duration}`).join(', ')
      : `all ${duration}`,
  }
}

export const $sxTransitionFast: ThemedStyle<SxProps> = () => ({
  transition: 'all 0.15s ease',
})

// Transition utilities - Slow (0.5s)
export const $transitionSlow = (properties?: string[]): ThemedStyle<CSSProperties> => () => {
  const duration = '0.5s ease'
  return {
    transition: properties 
      ? properties.map(p => `${p} ${duration}`).join(', ')
      : `all ${duration}`,
  }
}

export const $sxTransitionSlow: ThemedStyle<SxProps> = () => ({
  transition: 'all 0.5s ease',
})

// Clickable element styles
export const $clickable: ThemedStyle<CSSProperties> = () => ({
  cursor: 'pointer',
})

export const $sxClickable: ThemedStyle<SxProps> = () => ({
  cursor: 'pointer',
})

// Toggle container
export const $toggleContainer: ThemedStyle<CSSProperties> = () => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
})

/**
 * App overlay stacking (low → high).
 *
 * MUI defaults Menu/Popover/Autocomplete to ~1300, but our dialogs sit at
 * `modal` / `elevatedEditorDialog`. `floatingMenu` is the shared layer for
 * Select menus and Autocomplete poppers so they clear every dialog/overlay
 * except the admin nav drawer.
 *
 * Prefer theme defaults in ThemedMuiProvider + helpers in dialogs.ts over
 * one-off z-index numbers.
 */
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  overlay: 1000,
  tutorial: 9998,
  adminNav: 10001, // Fixed top nav bar
  /** Standard app Dialog (`$sxDialog`) */
  modal: 10002,
  /** Standard Dialog paper (`$sxDialogPaper`) */
  modalContent: 10003,
  /** PortalPopup editor shells (PinEditor, etc.) */
  portalPopup: 10010,
  /** Dialogs opened above PortalPopup */
  portalPopupDialog: 10030,
  /** Dialogs over GrapeJS / email editor */
  elevatedEditorDialog: 13000,
  /**
   * Select / Menu / Autocomplete — above any app dialog or PortalPopup.
   * Kept below adminNavDrawer so the burger menu still wins when open.
   * Selects that live inside that drawer use adminNavDrawerMenu instead.
   */
  floatingMenu: 13010,
  /**
   * Mobile burger Drawer (Modal + Paper).
   * Must sit above page overlays and any elevated editor dialogs (13000),
   * otherwise App/Org menu items receive backdrop clicks instead.
   */
  adminNavDrawer: 14000,
  /**
   * Select menus opened from inside the mobile admin nav drawer.
   * The drawer itself must stay above page menus; these options have to
   * clear the drawer paper or they render behind it.
   */
  adminNavDrawerMenu: 14100,
} as const

export const $sxZIndex = (layer: keyof typeof zIndex): ThemedStyle<SxProps> => () => ({
  zIndex: zIndex[layer],
})

// Responsive breakpoints
export const breakpoints = {
  mobile: 600,
  tablet: 960,
  desktop: 1200,
} as const

// Responsive utilities
export const $sxResponsivePadding = (
  desktop: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  mobile?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
): ThemedStyle<SxProps> => ({ spacing }) => ({
  p: { 
    xs: `${spacing[mobile || 'sm']}px`,
    md: `${spacing[desktop]}px` 
  },
})

export const $sxResponsiveMargin = (
  desktop: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  mobile?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
): ThemedStyle<SxProps> => ({ spacing }) => ({
  m: { 
    xs: `${spacing[mobile || 'sm']}px`,
    md: `${spacing[desktop]}px` 
  },
})

export const $sxResponsiveGap = (
  desktop: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  mobile?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
): ThemedStyle<SxProps> => ({ spacing }) => ({
  gap: { 
    xs: `${spacing[mobile || 'sm']}px`,
    md: `${spacing[desktop]}px` 
  },
})
